<template>
  <div
    class="calendar-wrap"
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.touch]: device.touch,
        [$style.isDragging]: isDragging,
        [$style.isResizing]: isResizing,
        [$style.isInteracting]: !!action,
        [$style.isSafari]: browser.isSafari,
        [$style.mobileFullView]: mobileFullView,
        [$style.scrollable]: !mobileFullView,
        [$style.dynamicPricingEnabled]: dynamicPricingMode,
        [$style.previewMode]: previewModeActive,
        [$style.previewLayout]: previewLayoutActive,
        'calendar-wrap--mobile-full-view': mobileFullView,
        'calendar-wrap--small-screen': $screen === 's',
        'calendar-wrap--preview': previewModeActive,
        'calendar-wrap--has-search': !!calendarSearchQuery
      }
    ]"
  >
    <Popovers
      v-if="isCalendarMounted"
      :calendarElement="calendarElement"
    />
    <div
      id="calendar-container"
      ref="calendarElement"
      :class="$style.calendar"
      class="vue-calendar"
    />
    <BaseSpinner v-show="isLoading" />
  </div>
</template>
<script lang="ts">
export default {
  name: 'Calendar',
};
</script>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useCalendarFiltersStore } from '@/stores/calendar-filters';
import { usePreCreateStore } from '@/stores/calendar-pre-create';
import { useCalendarPreviewStore } from '@/stores/calendar-preview';
import { browser, device } from '@/user-context';
import { calendarSearchQuery } from '../calendar-search';
import { useWatchers as useEventsWatchers } from './calendar-events';
import { useDragResize } from './calendar-interaction/drag-resize';
import { useWatchers as useResourcesWatchers } from './calendar-resources';
import { useCalendarScroll } from './calendar-scroll';
import { useCalendarSetup } from './calendar-setup';
import { useSubscription } from './calendar-subscription';
import Popovers from './popovers/index.vue';

const isCalendarMounted = ref(false);
const { renderCalendar, onCalendarMounted, isLoading } = useCalendarSetup();
const { isDragging, isResizing } = useDragResize();
const calendarElement = ref<HTMLElement | null>(null);

onMounted(() => {
  if (calendarElement.value) {
    renderCalendar(calendarElement.value);
  }

  useSubscription();
  useEventsWatchers();
  useResourcesWatchers();
});

onCalendarMounted(() => {
  isCalendarMounted.value = true;

  if (!calendarElement.value) {
    return;
  }

  useCalendarScroll(calendarElement.value);

  const wrapper = calendarElement.value.querySelector('.fc-view-harness');
  if (wrapper) {
    // Add class to tr elements not on the the whole hour
    Array.from(wrapper.querySelectorAll('.fc-timegrid-slot')).forEach((el) => {
      if (/00:00$/.test(el.getAttribute('data-time'))) {
        el.classList.add('fc-whole-hour');
      }
    });
  }
});

const { mobileFullView, dynamicPricingMode } = storeToRefs(
  useCalendarFiltersStore(),
);
const { action } = storeToRefs(usePreCreateStore());

const { previewModeActive, previewLayoutActive } = storeToRefs(
  useCalendarPreviewStore(),
);
</script>

<style lang="scss">
@import './styles/fullcalendar.css';
@import './styles/fullcalendar-common.css';
@import './styles/fullcalendar-timegrid.css';
@import './styles/fullcalendar-daygrid.css';
</style>

<style
  lang="scss"
  module
>
@use './styles/custom' as *;
</style>
